import { QueryClient, useMutation, useQueryClient } from 'react-query';
import { enviarOktonBank } from '../oktonbank';

// Todas as listas envelhecem a cada vínculo criado ou desfeito, mas nem todas
// mudam: conciliar um título não mexe na lista de Saques.
//
// A diferença pesa porque cada refetch é uma ida ao acoes.php, e o acoes.php
// leva o tempo de subir o IFLEX inteiro mesmo quando não há nada a fazer —
// medido em ~0,25s dentro do PHP, com a requisição vazia. Oito refetches por
// clique eram dois segundos de espera antes de a linha sair da tela.
//
// Então cada mutation diz o que ela mexeu: essas são buscadas na hora, e as
// demais ficam só marcadas como velhas, para se atualizarem quando alguém as
// pedir. Para o react-query o dado está inválido nas duas formas; o que muda é
// quando ele vai buscá-lo.
const LISTAS = [
  'extrato-bancario',
  'lancamentos-financeiros',
  'monitor-bancario-resumo',
  'lancamentos-abertos',
  'conciliacoes',
  'transferencias',
  'saques',
  'depositos',
  'dashboard',
] as const;

type Lista = typeof LISTAS[number];

function invalidarListas(queryClient: QueryClient, mexeu: Lista[]) {
  // O extrato e os conciliados mudam em toda operação desta tela: o
  // lançamento sai de pendente e o vínculo aparece do outro lado.
  const agora = new Set<Lista>([...mexeu, 'extrato-bancario', 'conciliacoes']);
  LISTAS.forEach(lista => {
    queryClient.invalidateQueries(
      lista,
      agora.has(lista)
        ? undefined
        : { refetchActive: false, refetchInactive: false },
    );
  });
}

interface VincularPayload {
  extrato: string;
  financeiro: string;
}

export interface ResultadoAutomatico {
  casados: number;
  extrato: number;
  // Documentos que podiam explicar o extrato — título já baixado, Saque,
  // Depósito e Transferência. Título em aberto fica de fora de propósito:
  // casar com ele o baixaria sem revisão.
  titulos: number;
}

// Vincula manualmente um lançamento do extrato a um título baixado.
export function useLinkConciliados() {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: VincularPayload) =>
      enviarOktonBank('OBConciliacao', 'vincular', { ...payload }),
    {
      onSuccess: () =>
        invalidarListas(queryClient, ['lancamentos-financeiros']),
    },
  );
}

// Baixa um título que estava em aberto com os dados da linha do extrato e já
// concilia os dois. É uma operação só no servidor: título baixado sem o
// vínculo, ou o contrário, deixaria a conciliação mentindo.
export function useBaixarEConciliar() {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: {
      extrato: string;
      financeiro: string;
      moeda: string;
      caixa: string;
      observacao: string;
    }) => enviarOktonBank('OBConciliacao', 'baixarEConciliar', { ...payload }),
    {
      onSuccess: () =>
        invalidarListas(queryClient, [
          'lancamentos-financeiros',
          'lancamentos-abertos',
        ]),
    },
  );
}

// O que fazer com o valor do extrato que os títulos escolhidos não cobrem.
export type DecisaoSobra = 'conciliado' | 'sobra' | 'pendente';

// Concilia um lançamento do extrato com vários títulos de uma vez. Título em
// aberto é baixado com a parte que lhe cabe antes de entrar no vínculo.
export function useVincularVarios() {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: {
      extrato: string;
      financeiros: string[];
      moeda: string;
      caixa: string;
      observacao: string;
      sobra: DecisaoSobra;
    }) =>
      enviarOktonBank<{ sobra: string; novoextrato: number; msg: string }>(
        'OBConciliacao',
        'vincularVarios',
        { ...payload, financeiros: payload.financeiros.join(',') },
      ),
    {
      onSuccess: () =>
        invalidarListas(queryClient, [
          'lancamentos-financeiros',
          'lancamentos-abertos',
        ]),
    },
  );
}

export function useUnlinkConciliados() {
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) =>
      enviarOktonBank('OBConciliacao', 'desvincular', { conciliacao: id }),
    { onSuccess: () => invalidarListas(queryClient, [...LISTAS]) },
  );
}

// Casa automaticamente por valor, sentido e data dentro da janela.
export function useAutoMatch() {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      conta,
      dataIni = '',
      dataFim = '',
    }: {
      conta: string;
      dataIni?: string;
      dataFim?: string;
    }) =>
      enviarOktonBank<ResultadoAutomatico>('OBConciliacao', 'automatico', {
        conta,
        dataini: dataIni,
        datafim: dataFim,
      }),
    { onSuccess: () => invalidarListas(queryClient, [...LISTAS]) },
  );
}

// Gera o título a partir do lançamento do extrato selecionado e já deixa os
// dois conciliados, no mesmo commit do back. Valor, sentido, data e Moeda saem
// do próprio extrato — a tela só manda o que não dá para deduzir.
export interface GerarFinanceiroPayload {
  extrato: string;
  cliente: string;
  documento: string;
  observacao?: string;
  centrocusto?: string;
  departamento?: string;
  direcionador?: string;
  // Em branco o IFLEX resolve pelo Caixa aberto, como sempre fez.
  caixa?: string;
  // Instrumento em que o título é baixado, entre as Moedas da Conta. Em
  // branco, a Moeda do próprio banco.
  moeda?: string;
}

export function useGerarFinanceiro() {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: GerarFinanceiroPayload) =>
      enviarOktonBank<{ id_fin: number }>('OBConciliacao', 'gerarFinanceiro', {
        ...payload,
      }),
    {
      onSuccess: () =>
        invalidarListas(queryClient, [
          'lancamentos-financeiros',
          'lancamentos-abertos',
        ]),
    },
  );
}

// Transferência entre contas próprias não é título: aparece nos dois extratos e
// tem cadastro próprio no IFLEX. Valor e data saem do lançamento selecionado.
export interface TransferenciaPayload {
  extrato: string;
  documento: string;
  origem: string;
  destino: string;
  planocontas?: string;
  observacao?: string;
}

export function useCadastrarTransferencia() {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: TransferenciaPayload) =>
      enviarOktonBank<{ id_tba: number }>('OBTransferencia', 'cadastrar', {
        ...payload,
      }),
    { onSuccess: () => invalidarListas(queryClient, ['transferencias']) },
  );
}

// Saque e Depósito também nascem do lançamento selecionado: a Conta, o valor e
// a data saem do extrato, e o documento já nasce conciliado no mesmo commit.
// O sentido decide qual dos dois cabe — o back recusa a combinação errada.
export interface DocumentoCaixaPayload {
  extrato: string;
  planocontas?: string;
  observacao?: string;
  caixa?: string;
}

export function useCadastrarSaque() {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: DocumentoCaixaPayload) =>
      enviarOktonBank<{ id_saq: number }>('OBSaque', 'cadastrar', {
        ...payload,
      }),
    { onSuccess: () => invalidarListas(queryClient, ['saques']) },
  );
}

export function useCadastrarDeposito() {
  const queryClient = useQueryClient();

  return useMutation(
    (
      payload: DocumentoCaixaPayload & {
        moeda: string;
        envelope?: string;
        // JSON com os ids dos cheques de terceiro depositados, quando houver.
        cheques?: string;
      },
    ) =>
      enviarOktonBank<{ id_dep: number }>('OBDeposito', 'cadastrar', {
        ...payload,
      }),
    { onSuccess: () => invalidarListas(queryClient, ['depositos']) },
  );
}

// Vincula o lançamento a uma Transferência já cadastrada — tipicamente a outra
// ponta, que aparece no extrato da outra Conta.
export function useLinkTransferencia() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ extrato, transferencia }: { extrato: string; transferencia: string }) =>
      enviarOktonBank('OBConciliacao', 'vincularTransferencia', {
        extrato,
        transferencia,
      }),
    { onSuccess: () => invalidarListas(queryClient, ['transferencias']) },
  );
}

// Vincula o lançamento a um Saque ou Depósito já cadastrado: movimentos da
// própria Conta que aparecem no extrato e não geram título.
export function useLinkDocumento() {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      extrato,
      tipo,
      documento,
    }: {
      extrato: string;
      tipo: 'saque' | 'deposito';
      documento: string;
    }) =>
      enviarOktonBank('OBConciliacao', 'vincularDocumento', {
        extrato,
        tipo,
        documento,
      }),
    { onSuccess: () => invalidarListas(queryClient, ['saques', 'depositos']) },
  );
}
